import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, User as UserIcon, Star, Loader2 } from "lucide-react";

interface ProfileOption {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  age_group: string | null;
  is_managed_child: boolean | null;
  roles: string[];
}

interface ProfileSelectorProps {
  user: User;
  onProfileSelected: (profileId: string) => void;
}

export const ProfileSelector = ({ user, onProfileSelected }: ProfileSelectorProps) => {
  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchSharedProfiles = async () => {
      if (!user.email) return;

      setIsLoading(true);
      try {
        // Check if this email has a shared group
        const { data: groupData } = await supabase
          .from("shared_email_groups")
          .select("id")
          .eq("email", user.email)
          .maybeSingle();

        if (!groupData) {
          // No shared email group, just use the logged-in user's profile
          setIsOpen(false);
          onProfileSelected(user.id);
          return;
        }

        // Fetch all profiles in this shared email group
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, avatar_url, age_group, is_managed_child")
          .eq("shared_email_group_id", groupData.id);

        if (!profilesData || profilesData.length <= 1) {
          // Only one profile or none, no need to show selector
          setIsOpen(false);
          onProfileSelected(user.id);
          return;
        }

        // Fetch roles for each profile
        const profilesWithRoles: ProfileOption[] = await Promise.all(
          profilesData.map(async (profile) => {
            const { data: rolesData } = await supabase
              .from("user_roles")
              .select("role")
              .eq("user_id", profile.id);
            
            return {
              ...profile,
              roles: rolesData?.map(r => r.role) || [],
            };
          })
        );

        setProfiles(profilesWithRoles);
        setIsOpen(true);
      } catch (error) {
        console.error("Error fetching shared profiles:", error);
        onProfileSelected(user.id);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedProfiles();
  }, [user]);

  const handleProfileSelect = (profileId: string) => {
    setIsOpen(false);
    onProfileSelected(profileId);
  };

  const getInitials = (profile: ProfileOption) => {
    const first = profile.first_name?.[0] || "";
    const last = profile.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const getAgeLabel = (ageGroup: string | null) => {
    switch (ageGroup) {
      case "kid": return "Kid Actor";
      case "tween": return "Tween Actor";
      case "teen": return "Teen Actor";
      case "adult": return "Adult";
      default: return "Actor";
    }
  };

  const isParent = (profile: ProfileOption) => 
    profile.roles.includes("parent") && !profile.is_managed_child;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Who's logging in?
          </DialogTitle>
          <DialogDescription>
            Select your profile to continue to CastReady.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-4">
          {profiles.map((profile) => (
            <Button
              key={profile.id}
              variant="outline"
              className="w-full justify-start h-auto py-4 px-4"
              onClick={() => handleProfileSelect(profile.id)}
            >
              <Avatar className="w-10 h-10 mr-3">
                <AvatarImage src={profile.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {getInitials(profile)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <p className="font-medium">
                  {profile.first_name} {profile.last_name}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {isParent(profile) ? (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Users className="w-3 h-3" />
                      Parent
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Star className="w-3 h-3" />
                      {getAgeLabel(profile.age_group)}
                    </Badge>
                  )}
                </div>
              </div>
              <UserIcon className="w-5 h-5 text-muted-foreground" />
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSelector;
